/*  FAQ Page — /faq
    ────────────────────────────────────────────────────────────────
    iter100.152 — Rebuilt as an aggregated, searchable, categorised
    answer library. Every FAQ across the marketing site (Homepage,
    Pricing, Product pages) is pulled into `faq_page.categories_json`
    so `/faq` becomes the single canonical answer surface.

    Behavioural state: category filter + fuzzy search + open accordion
    index. All content is CMS-driven; admins can add/remove categories
    or Q&As without a deploy. */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import CounsellorCallbackCard from "@/components/site/CounsellorCallbackCard";
import SeoHead from "@/components/site/SeoHead";
import { useProductPageCms } from "@/lib/useProductPageCms";
import {
    Sparkle, MagnifyingGlass, CaretDown, X, ChatCircleDots,
    Wallet, Crown, ShieldCheck, Heart, Stack, Stethoscope, Clock,
} from "@phosphor-icons/react";


const CMS_PAGE = "faq_page";

const ICON_MAP = {
    Sparkle, Wallet, Crown, ShieldCheck, Heart, Stack, Stethoscope, Clock,
    ChatCircleDots,
};
const resolveIcon = (name) => ICON_MAP[name] || Sparkle;


/* Small local util — highlights every case-insensitive occurrence of
   `term` inside `text` with a `<mark>` element. Returns children
   suitable for direct JSX rendering. */
function highlight(text, term) {
    if (!term) return text;
    const safe = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`(${safe})`, "ig");
    const parts = String(text).split(re);
    return parts.map((part, i) =>
        i % 2 === 1
            ? <mark key={i} className="bg-amber-200/60 text-foreground rounded px-0.5">{part}</mark>
            : <React.Fragment key={i}>{part}</React.Fragment>
    );
}


export default function FAQ() {
    const { t, arr, loading } = useProductPageCms(CMS_PAGE);
    const categories = arr("categories_json", []);
    const totalCount = useMemo(
        () => categories.reduce((n, c) => n + (c.items?.length || 0), 0),
        [categories],
    );

    const [activeCategoryId, setActiveCategoryId] = useState("all");
    const [query, setQuery] = useState("");
    const [openKey, setOpenKey] = useState(null);

    /* Filter categories against the current query. Search matches on
       both question and answer text; when a query is present we also
       auto-flip the category filter to "all" so results aren't hidden
       behind a category chip. */
    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        const scoped = activeCategoryId === "all"
            ? categories
            : categories.filter((c) => c.id === activeCategoryId);

        if (!q) {
            return scoped.map((c) => ({ ...c, items: c.items || [] }));
        }
        const src = q ? categories : scoped;
        return src
            .map((c) => ({
                ...c,
                items: (c.items || []).filter(
                    (it) =>
                        it.q.toLowerCase().includes(q) ||
                        it.a.toLowerCase().includes(q),
                ),
            }))
            .filter((c) => c.items.length > 0);
    }, [categories, activeCategoryId, query]);

    const matchCount = useMemo(
        () => results.reduce((n, c) => n + c.items.length, 0),
        [results],
    );

    // Auto-open first match when searching.
    useEffect(() => {
        if (query.trim() && results.length > 0 && results[0].items.length > 0) {
            setOpenKey(`${results[0].id}:0`);
        }
    }, [query, results]);

    if (loading) {
        return <div className="min-h-[60vh] bg-white" aria-hidden="true" data-testid="faq-loading" />;
    }

    return (
        <div data-testid="faq-page">
            <SeoHead
                page={CMS_PAGE}
                fallbackTitle="FAQ · Invisique™ Clear Aligners"
                fallbackDescription="The complete Invisique™ FAQ library — every question answered in one place."
            />

            <Hero
                t={t}
                query={query}
                onQueryChange={setQuery}
                totalCount={totalCount}
                matchCount={matchCount}
            />

            <CategoryStrip
                categories={categories}
                activeCategoryId={activeCategoryId}
                onCategoryChange={setActiveCategoryId}
                query={query}
            />

            <FaqResults
                results={results}
                query={query}
                openKey={openKey}
                onOpen={setOpenKey}
            />

            <ContactCta t={t} />
        </div>
    );
}


/* ================================================================
   Hero
   ================================================================ */
function Hero({ t, query, onQueryChange, totalCount, matchCount }) {
    return (
        <section className="relative overflow-hidden bg-background border-b border-border/60" data-testid="faq-hero">
            <div className="absolute inset-0 blue-grid-bg opacity-50 mask-fade-b pointer-events-none" aria-hidden="true" />
            <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-14 lg:pt-24 pb-12 lg:pb-16 relative">
                <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-white border border-border/70 shadow-sm mb-6" data-testid="faq-trust-pill">
                    <Sparkle size={13} weight="fill" className="text-amber-500" />
                    <span className="text-xs font-semibold text-foreground">
                        {t("hero.total_pill_prefix", "Answers, all in one place")}{" "}
                        <span className="text-muted-foreground font-normal">
                            · {totalCount} question{totalCount === 1 ? "" : "s"}
                        </span>
                    </span>
                </div>

                <div className="overline text-primary mb-4">{t("hero.eyebrow", "Answers, honest and complete")}</div>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
                    {t("hero.title_line1", "Every question,")}<br />
                    <span className="text-primary">{t("hero.title_line2", "answered on one page.")}</span>
                </h1>
                <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
                    {t("hero.blurb", "The Invisique™ library, pulled together in one place. Skim by category or search for the exact thing you're wondering about.")}
                </p>

                {/* Search input */}
                <div className="mt-8 max-w-xl relative">
                    <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                        <MagnifyingGlass size={18} weight="duotone" className="text-primary" />
                    </div>
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => onQueryChange(e.target.value)}
                        placeholder={t("hero.search_placeholder", "Search all questions…")}
                        className="w-full h-12 pl-11 pr-11 rounded-full border-2 border-border bg-white shadow-sm text-sm font-medium focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                        data-testid="faq-search"
                    />
                    {query && (
                        <button
                            type="button"
                            onClick={() => onQueryChange("")}
                            className="absolute inset-y-0 right-3 flex items-center text-muted-foreground hover:text-foreground"
                            aria-label="Clear search"
                            data-testid="faq-search-clear"
                        >
                            <X size={18} weight="bold" />
                        </button>
                    )}
                </div>

                {query && (
                    <div className="mt-3 text-sm text-muted-foreground" data-testid="faq-search-count">
                        {matchCount === 0
                            ? <span className="text-rose-600 font-medium">No matches found — try a shorter phrase.</span>
                            : <>Showing <span className="font-semibold text-foreground">{matchCount}</span> match{matchCount === 1 ? "" : "es"} for &ldquo;<span className="font-semibold text-foreground">{query}</span>&rdquo;</>
                        }
                    </div>
                )}
            </div>
        </section>
    );
}


/* ================================================================
   Category chips
   ================================================================ */
function CategoryStrip({ categories, activeCategoryId, onCategoryChange, query }) {
    if (!categories.length) return null;
    // While searching we disable category filtering — results span all.
    const disabled = Boolean(query.trim());
    return (
        <section className="sticky top-16 z-30 bg-background/95 backdrop-blur-md border-b border-border/60" data-testid="faq-category-strip">
            <div className="max-w-6xl mx-auto px-5 sm:px-8 py-3 overflow-x-auto">
                <div className="flex items-center gap-2 min-w-max">
                    <CategoryChip
                        label="All"
                        active={activeCategoryId === "all" || disabled}
                        onClick={() => onCategoryChange("all")}
                        testId="faq-category-all"
                    />
                    {categories.map((c) => {
                        const Icon = resolveIcon(c.icon);
                        return (
                            <CategoryChip
                                key={c.id}
                                icon={Icon}
                                label={c.name}
                                count={c.items?.length || 0}
                                active={!disabled && activeCategoryId === c.id}
                                onClick={() => onCategoryChange(c.id)}
                                testId={`faq-category-${c.id}`}
                            />
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function CategoryChip({ icon: Icon, label, count, active, onClick, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            aria-pressed={active}
            className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-xs font-semibold tracking-wide whitespace-nowrap transition-all ${
                active
                    ? "bg-primary text-primary-foreground shadow-sm ring-2 ring-primary/20"
                    : "bg-secondary/70 text-foreground/75 hover:bg-secondary hover:text-foreground"
            }`}
        >
            {Icon ? <Icon size={13} weight="fill" /> : null}
            <span>{label}</span>
            {typeof count === "number" && count > 0 && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? "bg-white/25" : "bg-white/70"}`}>
                    {count}
                </span>
            )}
        </button>
    );
}


/* ================================================================
   Results (categorised list of accordions)
   ================================================================ */
function FaqResults({ results, query, openKey, onOpen }) {
    if (results.length === 0) {
        return (
            <section className="py-16 lg:py-20 bg-background">
                <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-secondary grid place-items-center mx-auto mb-4">
                        <MagnifyingGlass size={24} weight="duotone" className="text-muted-foreground" />
                    </div>
                    <h2 className="font-display text-2xl font-bold">Nothing matches that phrase.</h2>
                    <p className="text-muted-foreground mt-2">
                        Try a shorter query or clear the search to browse all categories.
                    </p>
                </div>
            </section>
        );
    }

    return (
        <section className="py-14 lg:py-20 bg-background">
            <div className="max-w-3xl mx-auto px-5 sm:px-8 space-y-14">
                {results.map((cat) => {
                    const Icon = resolveIcon(cat.icon);
                    return (
                        <motion.div
                            key={cat.id}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.4 }}
                            data-testid={`faq-cat-${cat.id}`}
                        >
                            <div className="flex items-center gap-3 mb-5">
                                <div className="w-10 h-10 rounded-full bg-primary/10 grid place-items-center">
                                    <Icon size={17} weight="duotone" className="text-primary" />
                                </div>
                                <div>
                                    <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight">{cat.name}</h2>
                                    <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-muted-foreground mt-0.5">
                                        {cat.items.length} question{cat.items.length === 1 ? "" : "s"}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                {cat.items.map((it, i) => {
                                    const key = `${cat.id}:${i}`;
                                    const open = openKey === key;
                                    return (
                                        <div
                                            key={key}
                                            className={`border rounded-2xl overflow-hidden transition-colors ${open ? "border-primary/40 bg-card shadow-sm" : "border-border bg-card hover:border-primary/20"}`}
                                            data-testid={`faq-item-${cat.id}-${i}`}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => onOpen(open ? null : key)}
                                                className="w-full text-left p-5 flex items-start justify-between gap-3 hover:bg-secondary/30 transition-colors"
                                                aria-expanded={open}
                                                data-testid={`faq-toggle-${cat.id}-${i}`}
                                            >
                                                <h3 className="font-display font-semibold text-base sm:text-lg pr-4 leading-snug">
                                                    {highlight(it.q, query)}
                                                </h3>
                                                <CaretDown
                                                    size={18}
                                                    className={`text-primary shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                                                />
                                            </button>
                                            {open && (
                                                <div className="px-5 pb-5 -mt-1 text-sm text-muted-foreground leading-relaxed" data-testid={`faq-body-${cat.id}-${i}`}>
                                                    {highlight(it.a, query)}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </motion.div>
                    );
                })}
            </div>
        </section>
    );
}


/* ================================================================
   Bottom CTA — counsellor callback
   ================================================================ */
function ContactCta({ t }) {
    return (
        <section className="py-14 lg:py-20 bg-secondary/40 border-t border-border/60" data-testid="faq-cta">
            <div className="max-w-6xl mx-auto px-5 sm:px-8">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200/80 text-amber-700 text-[10px] font-bold tracking-[0.22em] uppercase">
                        <Sparkle size={11} weight="fill" /> {t("cta.eyebrow", "Still not sure?")}
                    </div>
                    <h2 className="font-display text-2xl sm:text-3xl lg:text-[2rem] font-bold tracking-tight leading-[1.1] mt-4 max-w-2xl mx-auto">
                        {t("cta.headline_prefix", "Have a specific question?")}{" "}
                        <span className="text-primary">{t("cta.headline_accent", "Talk to a real human.")}</span>
                    </h2>
                    <p className="text-muted-foreground mt-3 text-base max-w-xl mx-auto leading-relaxed">
                        {t("cta.blurb", "An Invisique™ Patient Engagement Specialist will call back to answer your exact question and, if you want, help you find the right partner clinic near you.")}
                    </p>
                </div>
                <CounsellorCallbackCard />
            </div>
        </section>
    );
}
